import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { UpdateTenantForm } from '@/backoffice/features/tenants/components/update-tenant/update-tenant-form';
import {
  TTenantOwnerOption,
  TUpdateTenantDrawerContentProps,
  TUpdateTenantFormSchema,
} from '@/backoffice/features/tenants/components/update-tenant/update-tenant.types';
import { getUpdateTenantFormSchema } from '@/backoffice/features/tenants/components/update-tenant/update-tenant.validation';
import {
  useReadTenantForUpdateQuery,
  useUpdateBackofficeTenantMutation,
} from '@/backoffice/redux/api/backoffice/backoffice-api';
import { BillingSewBillingTypeEnum } from '@/backoffice/types/types.generated';
import {
  applySewCustomerParties,
  getSewCustomerPartiesMode,
  SEW_CUSTOMER_PARTIES_MODE,
  SEW_CUSTOMER_PARTIES_MODE_FIELD,
  SEW_METADATA_KEYS,
} from '@/reseller/sew/metadata/sew-metadata.constants';
import { Form } from '@/shared/components/ui/form/form';
import { TSubmittedData } from '@/shared/components/ui/form/form.types';
import { Loader } from '@/shared/components/ui/loader/loader';
import { UserAdditionalInfo } from '@/shared/features/rbac/components/user-select/user-additional-info/user-additional-info';
import { getFullname } from '@/shared/utils/format';
import { getReseller } from '@/shared/utils/reseller/reseller';
import { showToastError, showToastSuccess } from '@/shared/utils/toast';

export const UpdateTenantDrawerContent: React.FC<
  TUpdateTenantDrawerContentProps
> = ({ tenantId, handleClose }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useReadTenantForUpdateQuery({ tenantId });
  const [updateTenant] = useUpdateBackofficeTenantMutation();

  const tenant = data?.readTenant;
  const objectVersion = tenant?.objectVersion;
  const ssoEnabled = tenant?.ssoEnabled ?? false;
  const isSew = getReseller() === 'sew';

  const ownerOptions = useMemo<TTenantOwnerOption[]>(
    () =>
      (tenant?.users.items ?? [])
        .filter((user) => user != null)
        .map((user) => ({
          value: user.userId,
          label: getFullname(user),
          additionalInfo: (
            <UserAdditionalInfo user={user} ssoEnabled={ssoEnabled} />
          ),
        })),
    [tenant, ssoEnabled],
  );

  const handleSubmit = async (
    submitted: TSubmittedData<TUpdateTenantFormSchema>,
  ) => {
    try {
      const { [SEW_CUSTOMER_PARTIES_MODE_FIELD]: mode, ...dirtyData } =
        submitted.dirtyData;
      const tenantUpdate = {
        ...dirtyData,
        objectVersion: objectVersion! + 1,
      };

      // metaData is a JSON blob — send it whole when any field changed so we
      // don't drop sibling keys with a partial update. Switching the parties
      // choice alone dirties no metaData field, but has to remove the ship-to
      // and bill-to keys.
      if (submitted.dirtyData.metaData || mode) {
        tenantUpdate.metaData = applySewCustomerParties(
          submitted.data.metaData,
          submitted.data[SEW_CUSTOMER_PARTIES_MODE_FIELD] ===
            SEW_CUSTOMER_PARTIES_MODE.separate,
        );
      }

      await updateTenant({
        tenantId,
        tenant: tenantUpdate,
      }).unwrap();
      showToastSuccess(t('backoffice:edit_tenant.update_success'));
      handleClose();
    } catch {
      showToastError(t('backoffice:edit_tenant.update_error'));
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  // Billing Type is mandatory for SEW; default legacy tenants that predate the
  // field to "Customer Billing" so the form is valid without re-collecting it.
  const metaData =
    isSew && tenant?.metaData
      ? {
          [SEW_METADATA_KEYS.billingType]:
            BillingSewBillingTypeEnum.CustomerBilling,
          ...tenant.metaData,
        }
      : (tenant?.metaData ?? undefined);

  return (
    <Form<TUpdateTenantFormSchema>
      schema={getUpdateTenantFormSchema(isSew)}
      defaultValues={{
        ownerId: tenant?.ownerId ?? '',
        groupId: tenant?.groupId ?? '',
        [SEW_CUSTOMER_PARTIES_MODE_FIELD]: getSewCustomerPartiesMode(metaData),
        metaData,
      }}
      onSubmit={handleSubmit}
    >
      {() => (
        <UpdateTenantForm
          ownerOptions={ownerOptions}
          handleClose={handleClose}
        />
      )}
    </Form>
  );
};
